<template>
  <AccountCard :title="$t('pages.account.settings.sections.phone_number.title')">
    <hr class="my-6" />
    <div class="flex flex-row flex-wrap items-center gap-4">
      <VForm
        :validation-schema="Schema"
        :initial-values="Schema.getDefault()"
        v-slot="{ handleSubmit }"
        as="div"
        class="flex-1 min-w-[15rem]"
      >
        <form @submit="handleSubmit($event, mutate)" class="max-w-lg">
          <h4 class="font-semibold">
            {{ $t("pages.account.settings.sections.phone_number.subtitle") }}
          </h4>
          <CommonAlert v-if="error" type="error" :content="error.message" class="mb-6 mt-4" />
          <div class="w-full mt-2">
            <div class="flex flex-row flex-wrap gap-x-2 md:gap-y-6 md:pt-4">
              <BaseVTextInput
                type="text"
                :label="$t('form.new_phone_number')"
                name="telephone_number"
                class="flex-1 basis-full"
              />
              <BaseVTextInput type="password" :label="$t('form.password')" name="password" class="flex-1 basis-full" />
            </div>

            <div class="flex flex-row flex-wrap gap-4 items-center mt-8">
              <BaseButton
                type="submit"
                class="w-auto max-w-full whitespace-nowrap text-[1rem]"
                :loading="pending"
                size="medium"
              >
                {{ $t("pages.account.buttons.change_phone_number") }}
              </BaseButton>

              <div class="flex-1 basis-1/3 text-primary shrink-0">
                <div class="flex flex-row gap-2 items-center" v-if="success">
                  <CheckCircleIcon class="w-[1.5em] h-[1.5em] shrink-0" />
                  <span>{{ $t("pages.account.texts.changes_saved") }}</span>
                </div>
              </div>
            </div>
          </div>
        </form>
      </VForm>
    </div>
  </AccountCard>
</template>

<script setup lang="ts">
import { CheckCircleIcon } from "@heroicons/vue/solid";
import { AxiosError } from "axios";
import * as yup from "yup";
import type { User } from "~/entities/User";
import AccountCard from "../components/account-card.vue";

interface AccountSettingsPersonalDetailsProps {
  profileDetails: User.ProfileDetails;
}
const props = defineProps<AccountSettingsPersonalDetailsProps>();
const localProfile = reactive({ ...props.profileDetails }); // Create reactive local copy

const { t } = useI18n();
const phoneRegex = usePhoneRegex();

const Schema = yup.object({
  type: yup.string().optional().default("telephone-number"),
  telephone_number: yup
    .string()
    .matches(phoneRegex.value, {
      message: t("validationErrors.validPhoneNumber"),
    })
    .required(t("validationErrors.phoneRequired")),
  password: yup.string().required(),
});
type Schema = yup.InferType<typeof Schema>;

Schema.getDefault = () => ({
  type: "telephone-number",
  telephone_number: localProfile.telephone_number,
});

const credentialsCopy = reactive({});

const error = ref<Error>();
const { mutate, pending, success } = useMutation({
  mutationFn: async (credentials: Schema) => {
    Object.assign(credentialsCopy, credentials);
    return useAxios()
      .put("/profile/update", credentials)
      .then((res) => res.data);
  },
  onError(err) {
    error.value = err;
    if (err instanceof AxiosError) error.value = new Error(err.response?.data.message);
  },
  onSuccess() {
    error.value = undefined;
    Object.assign(localProfile, credentialsCopy);
    delete localProfile.type;

    const profileStore = useProfileStore();
    profileStore.setProfile(localProfile);
  },
});
</script>
